import React from 'react';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { CookiesProvider } from 'react-cookie';
import { Provider as ReduxStoreProvider } from 'react-redux';
import { BrowserRouter } from 'react-router';

import CookieBanner from '@/components/CookieBanner';
import GlobalModal from '@/components/GlobalModal';
import ResponseMessage from '@/components/ResponseMessage';
import SymbolSelector from '@/components/SymbolSelector';
import { MAINTENANCE_MODE_FLAG } from '@/constants/featureFlags';
import { darkModeTheme } from '@/constants/mui';
import { SnackbarNotificationsProvider } from '@/context/SnackbarNotificationsContext';
import { AddToAppContextProvider } from '@/features/Dashboard/context/AppContext';
import { WidgetAnimationProvider } from '@/features/Dashboard/context/WidgetAnimation/provider';
import Listeners from '@/features/Listeners';
import { MaintenanceMode } from '@/features/MaintenanceMode';
import { TrackingLister } from '@/features/Tracking';
import { IdentifyPostHogProvider } from '@/features/Tracking/context/PostHogContext';
import { useFeatureFlagRefresh } from '@/hooks/useFeatureFlagRefresh';

import AppRoutes from './AppRoutes';
import { store } from './store';

const queryClient = new QueryClient();

const App: React.FC = () => {
  const isMaintenanceMode = useFeatureFlagEnabled(MAINTENANCE_MODE_FLAG);
  useFeatureFlagRefresh();
  return (
    <CookiesProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={darkModeTheme}>
          <ReduxStoreProvider store={store}>
            <AddToAppContextProvider>
              <SnackbarProvider
                Components={{
                  loading: ResponseMessage,
                  error: ResponseMessage,
                  success: ResponseMessage,
                  info: ResponseMessage
                }}
                maxSnack={5}
              >
                <SnackbarNotificationsProvider>
                  <CssBaseline />
                  <BrowserRouter>
                    <IdentifyPostHogProvider>
                      <Listeners />
                      <SymbolSelector />

                      <GlobalModal />
                      <CookieBanner />
                      <WidgetAnimationProvider>
                        <TrackingLister>
                          {isMaintenanceMode ? <MaintenanceMode /> : <AppRoutes />}
                        </TrackingLister>
                      </WidgetAnimationProvider>
                    </IdentifyPostHogProvider>
                  </BrowserRouter>
                </SnackbarNotificationsProvider>
              </SnackbarProvider>
            </AddToAppContextProvider>
          </ReduxStoreProvider>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </CookiesProvider>
  );
};

export default App;
